import { createClient } from '@supabase/supabase-js'
import { CLE_PUBLISHABLE, URL_SUPABASE } from './supabase'

/**
 * Mot de passe oublié et changement de mot de passe.
 *
 * Un seul projet Supabase sert tous les comptes SwiftEats (marchands, dispatch,
 * livreurs, clients) : la page qui reçoit le lien de l'e-mail est donc UNE seule,
 * dans la console marchand, à `/reinitialiser` (voir
 * `SwiftEats/docs/REINITIALISATION-MOT-DE-PASSE.md`). Cette console s'y renvoie.
 */

/**
 * URL EXACTE de la page de réinitialisation, à déclarer dans « Redirect URLs » de
 * Supabase Auth. Constante, pas `window.location.origin` : le dispatch vit sur un
 * autre sous-domaine, et une adresse qui dépendrait de l'origine obligerait à en
 * déclarer une par console.
 */
export const URL_REINITIALISATION = 'https://eats.yiriwa-group.com/reinitialiser'

/**
 * Réponse NEUTRE, la même que l'adresse existe ou non : dire « aucun compte avec
 * cette adresse » permettrait de sonder qui a un compte.
 */
export const MESSAGE_LIEN_ENVOYE =
  "Si un compte existe pour cette adresse, un e-mail avec un lien vient d'être envoyé. " +
  'Regardez aussi dans les courriers indésirables. Le lien ne sert qu’une fois.'

export const emailValide = (e: string): boolean => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e.trim())

/**
 * Un client SANS session ni stockage, en flux IMPLICITE.
 *
 * Pourquoi pas le client PKCE de la console : en PKCE, le lien de l'e-mail revient
 * avec un `?code=` qui ne s'échange que dans le NAVIGATEUR ET SUR LE SOUS-DOMAINE
 * qui a fait la demande (le « code verifier » y est rangé). Or la page de
 * réinitialisation est sur un autre domaine : l'échange y échouerait toujours. En
 * flux implicite, le lien porte ses jetons dans le fragment (`#access_token=…`) et
 * marche depuis n'importe quel appareil — comme pour les apps mobiles.
 */
const clientDemande = () =>
  createClient(URL_SUPABASE, CLE_PUBLISHABLE, {
    auth: {
      flowType: 'implicit',
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'sb-swifteats-demande-reinit',
    },
  })

interface ErreurAuth {
  message?: string | undefined
  code?: string | undefined
  status?: number | undefined
}

/** Phrase de repli : ce que dit `traduireErreurAuth` quand elle ne reconnaît pas l'erreur. */
export const MESSAGE_GENERIQUE = 'Opération impossible pour le moment. Réessayez dans un instant.'

/** Erreurs de Supabase Auth (en anglais) → phrases. */
export function traduireErreurAuth(err: ErreurAuth | null | undefined): string {
  const m = (err?.message ?? '').toLowerCase()
  const c = (err?.code ?? '').toLowerCase()
  if (err?.status === 429 || c.includes('rate_limit') || m.includes('rate limit') || m.includes('security purposes')) {
    return 'Trop de demandes en peu de temps. Patientez quelques minutes avant de réessayer.'
  }
  if (c === 'same_password' || m.includes('different from the old password')) {
    return "Choisissez un mot de passe différent de l'ancien."
  }
  if (c === 'weak_password' || m.includes('password should be at least') || m.includes('weak')) {
    return 'Mot de passe trop faible : au moins 6 caractères.'
  }
  if (c === 'otp_expired' || m.includes('expired') || m.includes('invalid or has expired')) {
    return 'Ce lien a expiré ou a déjà servi. Redemandez un lien depuis l’écran de connexion.'
  }
  if (m.includes('session') && (m.includes('missing') || m.includes('not found') || m.includes('expired'))) {
    return 'Votre session a expiré. Reconnectez-vous puis réessayez.'
  }
  if (m.includes('sending') && m.includes('email')) {
    return "L'e-mail n'a pas pu être envoyé. Réessayez plus tard ou contactez l'équipe SwiftEats."
  }
  if (m.includes('failed to fetch') || m.includes('network') || m.includes('load failed')) {
    return 'Connexion impossible. Vérifiez votre réseau et réessayez.'
  }
  return MESSAGE_GENERIQUE
}

/** Demande l'e-mail de réinitialisation. Lève avec une phrase si l'envoi échoue. */
export async function demanderReinitialisation(email: string): Promise<void> {
  if (!emailValide(email)) throw new Error('Saisissez une adresse e-mail valide.')
  const { error } = await clientDemande().auth.resetPasswordForEmail(email.trim().toLowerCase(), {
    redirectTo: URL_REINITIALISATION,
  })
  if (error) throw new Error(traduireErreurAuth(error))
}

export const LONGUEUR_MINI_MOT_DE_PASSE = 6

/** Contrôle de saisie d'un nouveau mot de passe ; `null` si tout va bien. */
export function verifierNouveauMotDePasse(nouveau: string, confirmation: string): string | null {
  if (nouveau.length < LONGUEUR_MINI_MOT_DE_PASSE) {
    return `Le mot de passe doit faire au moins ${LONGUEUR_MINI_MOT_DE_PASSE} caractères.`
  }
  if (nouveau !== confirmation) return 'Les deux saisies ne correspondent pas.'
  return null
}

/**
 * Compte né avec un mot de passe temporaire (marque `mdp_temporaire` posée à la
 * création par les edge functions `create-*-account`, `create-merchant-staff` et
 * `invite-member`). Tant qu'elle est là, la console invite à choisir un mot de passe
 * personnel — elle ne l'impose pas : on peut reporter, et l'invitation revient.
 */
export function motDePasseTemporaire(
  utilisateur: { user_metadata?: Record<string, unknown> | null } | null | undefined,
): boolean {
  return utilisateur?.user_metadata?.mdp_temporaire === true
}

/**
 * Ce qu'on envoie à `updateUser` pour changer le mot de passe : le mot de passe ET
 * l'effacement de la marque, en UN seul appel (deux appels laisseraient une
 * fenêtre où le mot de passe est changé mais l'invitation persiste, ou l'inverse).
 */
export function attributsNouveauMotDePasse(nouveau: string): {
  password: string
  data: { mdp_temporaire: false }
} {
  return { password: nouveau, data: { mdp_temporaire: false } }
}
